// ============================================================================
// MANUAL DE PROCESSOS FINANCEIROS — Grupo Correia (conteúdo)
// ============================================================================
// Transcrição do PDF v1.0/2026 escrito pelo Felipe (82 páginas, Google Docs),
// incorporada ao portal em 2026-09-04. NÃO é um PDF embutido num iframe: é
// dado estruturado, e é isso que dá busca, índice, link direto para um
// capítulo e impressão — coisas que o PDF dentro de um `<embed>` não dá.
//
// ⚠️ O FORMATO ESPELHA O DOCUMENTO, e isso é o que o torna barato de manter: o
//    manual do Felipe repete os MESMOS blocos em todo capítulo (🎯 objetivo,
//    👤 responsável, ⏰ quando, 🔵 como fazer, 🟡/🔴 atenção, 💡 exemplo,
//    🟢 checklist de conclusão). Cada um virou um `tipo` de bloco renderizado
//    por `BlocosManual.js`. Manual novo que siga o mesmo padrão não precisa de
//    componente novo — só de um arquivo como este.
//
// ⚠️ AS IMAGENS mantêm o nome `p<página>-<n>.webp` da extração do PDF de
//    propósito: é o que amarra cada print à PÁGINA do documento original e
//    permite conferir uma legenda contra a fonte anos depois. Nome "bonito"
//    (`sangria-total-passo-3.webp`) se descolaria da fonte no primeiro capítulo
//    que mudasse de ordem. Elas NÃO ficam em `public/` — ver o porquê (dado
//    pessoal nos prints) em `server/biblioteca/biblioteca-acervo.js`.
//
// ⚠️ IMAGEM REPETIDA APONTA PARA O MESMO ARQUIVO. As telas "Menu Gerente",
//    "número do gerente" e "senha do gerente" aparecem em 6 capítulos; o
//    arquivo é um só (`p10-1`, `p11-1`, `p11-2`) e a extração deduplicou por
//    hash — 119 imagens no PDF, 89 no disco. Por isso um capítulo do meio do
//    manual cita legitimamente uma imagem `p10-*`.
//
// ⚠️ FIDELIDADE: o texto é o do Felipe, não uma reescrita. As duas únicas
//    intervenções foram (1) "Prossegur" → "Prosegur" no cap. 14, que o próprio
//    documento escreve certo nas outras 4 ocorrências, e (2) pontuação de lista.
//    Onde o original tem uma imprecisão de conteúdo — o cap. 07 rotula
//    "COMO FAZER - FLEX" um passo que acontece no POS —, ela foi MANTIDA: é o
//    documento oficial, e corrigir conteúdo por conta própria criaria uma
//    segunda versão da verdade. Registrado no PLANO para o Felipe decidir.
// ============================================================================

const manual = {
    id: 'financeiro-processos',
    titulo: 'Manual de Processos Financeiros',
    subtitulo: 'Grupo Correia',
    versao: '1.0',
    ano: '2026',
    aplicacao: 'Uso interno — Restaurantes Grupo Correia',
    autor: 'Felipe',

    finalidade:
        'Este manual estabelece os procedimentos financeiros padronizados do Grupo Correia, ' +
        'orientando a execução das rotinas operacionais e fortalecendo os controles, a segurança ' +
        'e a correta gestão financeira dos restaurantes.',

    orientacaoDeUso:
        'Os procedimentos descritos neste manual devem ser utilizados como referência para a ' +
        'execução das rotinas financeiras e para o treinamento das equipes responsáveis.',

    padrao:
        'Em caso de atualização de processo, a versão mais recente deste manual deverá ser ' +
        'utilizada como referência.',

    capitulos: [
        // ====================================================================
        {
            numero: '01',
            id: 'abertura-do-dia',
            titulo: 'Abertura do Dia',
            objetivo:
                'Garantir que todos os caixas (POS) estejam online, com a data correta e ' +
                'devidamente abertos para o início do novo dia operacional.',
            responsavel: 'Gerente de Plantão ou Coordenador de Abertura',
            quando:
                'Lojas Shopping: antes da abertura do restaurante. ' +
                'Lojas Drive com madrugada: imediatamente após o fechamento do dia, às 00:00.',
            sistemas: ['Waystation'],
            blocos: [
                {
                    tipo: 'passo',
                    texto: 'Acesse o Waystation e confira:',
                    itens: [
                        'Se todos os caixas (POS) estão online',
                        'Se a Business Day corresponde à data atual',
                    ],
                    imagens: [
                        { arquivo: 'p06-1.webp', legenda: 'No Waystation, confira a coluna de estado dos POS e a Business Day no canto superior direito.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto: 'Só prossiga após confirmar que todos os POS estão online e a Business Day está correta.',
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    titulo: 'Data incorreta?',
                    texto: 'Não realize a abertura. Abra um chamado na Invigate/Sonda para regularização da Business Day.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Realize a abertura',
                    texto:
                        'Selecione todos os caixas (é possível clicar em cima de cada POS/NGK ou clicar em ' +
                        '“select all” no fim da página). Ao ser selecionado, o POS fica roseado → clique em ' +
                        '“Operações de Gerente”.',
                    imagens: [
                        { arquivo: 'p07-1.webp', legenda: 'Todos os POS selecionados (em destaque) e o botão “Operações de Gerente”.' },
                    ],
                },
                {
                    tipo: 'passo',
                    titulo: 'Confirme nº do gerente e senha',
                    texto: 'Clique em “Abertura do Dia Forçado” → selecione a data correspondente à data da abertura e confirme.',
                    imagens: [
                        { arquivo: 'p08-1.webp', legenda: 'Enter the manager id — número do gerente.' },
                        { arquivo: 'p08-2.webp', legenda: 'Enter the password — senha do gerente.' },
                        { arquivo: 'p08-3.webp', legenda: 'Botões de abertura do dia (Regular e Forçado).' },
                        { arquivo: 'p08-4.webp', legenda: 'A data da abertura no formato yyyyMMdd.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'O sistema exibirá uma mensagem com os POS que foram abertos. ' +
                        'Confira se todos os caixas selecionados aparecem na confirmação.',
                    imagens: [
                        { arquivo: 'p09-1.webp', legenda: 'Confirmação do Waystation: “abertura do dia da loja forçada bem sucedida”.' },
                    ],
                },
            ],
            conclusao: {
                titulo: 'Abertura do dia concluída',
                itens: [
                    'Todos os POS online',
                    'Business Day na data atual',
                    'Abertura confirmada pelo sistema',
                ],
            },
        },

        // ====================================================================
        {
            numero: '02',
            id: 'abertura-de-operador',
            titulo: 'Abertura de Operador',
            objetivo: 'Realizar a abertura do operador no POS, registrando corretamente o valor do fundo utilizado.',
            responsavel: 'Gerente de Plantão',
            quando: 'Antes do início da operação do POS.',
            sistemas: ['POS'],
            blocos: [
                {
                    tipo: 'passo',
                    texto: 'No POS, aperte Menu Gerente.',
                    imagens: [
                        { arquivo: 'p10-1.webp', legenda: 'Tela principal do POS — o botão Menu Gerente fica no topo à direita.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                        'Aperte em Abre Operador',
                    ],
                    imagens: [
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                        { arquivo: 'p11-3.webp', legenda: 'Menu Gerente com a opção Abre Operador.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do funcionário',
                        'Coloque o valor do fundo e depois “entra”',
                        'Confirme o login do operador e o valor do fundo de troco e aperte SIM',
                    ],
                    imagens: [
                        { arquivo: 'p12-1.webp', legenda: 'Informe o número do funcionário.' },
                        { arquivo: 'p12-2.webp', legenda: 'Informe o valor do fundo de troco.' },
                        { arquivo: 'p12-3.webp', legenda: 'Confirmação do operador e do fundo de troco.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'O comprovante será impresso e o nome do operador constará na parte superior do POS. ' +
                        'Se houver um colaborador posicionado no POS, ele deverá assinar o comprovante.',
                    imagens: [
                        { arquivo: 'p13-1.webp', legenda: 'Comprovante impresso e o nome do operador no topo da tela do POS.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto: 'O comprovante deve permanecer dentro da gaveta de dinheiro do respectivo POS.',
                },
            ],
            conclusao: {
                titulo: 'Abertura de operador concluída',
                itens: [
                    'Fundo registrado no POS',
                    'Operação confirmada',
                    'Comprovante emitido',
                    'Assinatura do colaborador, quando aplicável',
                    'Comprovante armazenado na gaveta de dinheiro',
                ],
            },
        },

        // ====================================================================
        {
            numero: '03',
            id: 'caixa-surpresa',
            titulo: 'Caixa Surpresa',
            objetivo:
                'Conferir o valor em dinheiro existente no POS e identificar possíveis diferenças ' +
                'entre o valor físico e o registrado no sistema.',
            responsavel: 'Gerente de Plantão ou Gerente de Quiosque',
            sistemas: ['POS'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto: 'O Caixa Surpresa deve ser realizado junto ao colaborador que estiver operando o POS.',
                },
                {
                    tipo: 'passo',
                    texto: 'No POS, clique em Menu Gerente.',
                    imagens: [
                        { arquivo: 'p10-1.webp', legenda: 'Tela principal do POS — o botão Menu Gerente fica no topo à direita.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                        'Acesse Relatórios Gerenciais',
                    ],
                    imagens: [
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                        { arquivo: 'p15-3.webp', legenda: 'Menu Gerente com a opção Relatórios Gerenciais.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Aperte em Caixa Surpresa.',
                    imagens: [
                        { arquivo: 'p16-1.webp', legenda: 'Relatórios Gerenciais — a opção Caixa Surpresa.' },
                    ],
                },
                {
                    tipo: 'passo',
                    titulo: 'Imprima o relatório',
                    texto:
                        'Após a impressão e com o relatório em mãos, conte todo o dinheiro existente na gaveta. ' +
                        'Desconte o valor do fundo para encontrar o valor referente às vendas em dinheiro.',
                    imagens: [
                        { arquivo: 'p17-1.webp', legenda: 'Relatório de Caixa Surpresa — a linha DINHEIRO é a que se compara.' },
                    ],
                },
                {
                    tipo: 'exemplo',
                    linhas: [
                        { rotulo: 'Dinheiro na gaveta', valor: 'R$ 230,00' },
                        { rotulo: 'Fundo', valor: 'R$ 100,00' },
                        { rotulo: 'Dinheiro apurado', valor: 'R$ 130,00', destaque: true },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'Compare o valor encontrado com a linha DINHEIRO do relatório de Caixa Surpresa. ' +
                        'Registre no relatório o valor contado e a diferença encontrada.',
                },
                {
                    tipo: 'exemplo',
                    linhas: [
                        { rotulo: 'Linha dinheiro — fita caixa surpresa', valor: 'R$ 131,30' },
                        { rotulo: 'Dinheiro apurado', valor: 'R$ 130,00' },
                        { rotulo: 'Diferença', valor: '− R$ 1,30', destaque: true },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    texto: 'Toda diferença encontrada deve ser registrada no relatório.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Após a conferência',
                    itens: [
                        'Gerente responsável assina o relatório',
                        'Operador do POS assina o relatório',
                    ],
                    iconeItens: 'assinatura',
                    texto: 'O relatório assinado deve permanecer na gaveta de dinheiro do POS.',
                    imagens: [
                        { arquivo: 'p19-1.webp', legenda: 'Relatório de Caixa Surpresa assinado, com o valor contado e a diferença registrados.' },
                    ],
                },
                {
                    tipo: 'nota',
                    icone: 'anexo',
                    texto: 'Ao final do plantão: retirar o relatório da gaveta e anexá-lo ao financeiro correspondente.',
                },
            ],
            conclusao: {
                titulo: 'Caixa Surpresa concluído',
                itens: [
                    'Dinheiro contado sem o fundo',
                    'Valor comparado com o sistema',
                    'Diferença registrada, quando houver',
                    'Gerente e operador assinaram',
                    'Relatório guardado e posteriormente anexado ao financeiro',
                ],
            },
        },

        // ====================================================================
        {
            numero: '04',
            id: 'sangria-parcial',
            titulo: 'Sangria Parcial',
            objetivo:
                'Realizar a retirada parcial de dinheiro do POS de forma segura, garantindo o ' +
                'correto registro e depósito do valor no cofre.',
            responsavel: 'Gerente de Plantão ou Gerente de Quiosque',
            sistemas: ['POS'],
            blocos: [
                {
                    tipo: 'passo',
                    texto: 'No POS, clique em Menu Gerente.',
                    imagens: [
                        { arquivo: 'p10-1.webp', legenda: 'Tela principal do POS — o botão Menu Gerente fica no topo à direita.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                        'Aperte em Sangria Parcial',
                    ],
                    imagens: [
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                        { arquivo: 'p22-3.webp', legenda: 'Menu Gerente com a opção Sangria Parcial.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'Escaneie o código de barras do envelope de sangria e aperte Enter. Em caso de não ' +
                        'leitura automática do código, os 9 dígitos devem ser inseridos manualmente. ' +
                        'Confirme o número do envelope e aperte em SIM.',
                    imagens: [
                        { arquivo: 'p23-1.webp', legenda: 'Campo do código do envelope, com teclado na tela para digitação manual.' },
                        { arquivo: 'p23-2.webp', legenda: 'Código do envelope digitado.' },
                        { arquivo: 'p23-3.webp', legenda: 'Confirmação do número do envelope (banana).' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'O sistema indicará o valor disponível para retirada.',
                    imagens: [
                        { arquivo: 'p24-1.webp', legenda: 'Valor máximo disponível para sangria.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'Informe o valor que deseja sangrar e confirme a operação. Após a confirmação, ' +
                        'o POS imprimirá o comprovante da sangria.',
                    imagens: [
                        { arquivo: 'p25-1.webp', legenda: 'Valor da sangria informado.' },
                    ],
                },
                {
                    tipo: 'nota',
                    icone: 'assinatura',
                    texto: 'O comprovante deve ser assinado por:',
                    itens: ['Gerente responsável', 'Colaborador que estiver operando o POS'],
                },
                {
                    tipo: 'passo',
                    texto: 'Coloque no envelope:',
                    itens: ['O valor retirado do caixa', 'O comprovante da sangria assinado'],
                    imagens: [
                        { arquivo: 'p26-1.webp', legenda: 'Comprovante da sangria parcial impresso no POS.' },
                    ],
                },
                {
                    tipo: 'nota',
                    icone: 'lacre',
                    texto: 'Lacre o envelope imediatamente e na presença do colaborador.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Deposite no cofre',
                    texto: 'Após finalizar a sangria, encaminhe-se diretamente à gerência e deposite o envelope no cofre.',
                    imagens: [
                        { arquivo: 'p27-1.webp', legenda: 'Envelope de sangria lacrado, com o comprovante dentro.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    texto:
                        'A sangria deve ser depositada no cofre imediatamente após sua finalização. ' +
                        'Em hipótese alguma o envelope deve ser colocado no bolso, guardado em outro local ' +
                        'ou permanecer com o gerente.',
                },
            ],
            conclusao: {
                titulo: 'Sangria parcial concluída',
                itens: [
                    'Envelope identificado',
                    'Valor retirado conforme registrado no POS',
                    'Comprovante assinado',
                    'Dinheiro e comprovante colocados no envelope',
                    'Envelope lacrado na presença do colaborador',
                    'Sangria depositada imediatamente no cofre',
                ],
            },
        },

        // ====================================================================
        {
            numero: '05',
            id: 'sangria-total-fechamento-operador',
            titulo: 'Sangria Total / Fechamento de Operador',
            objetivo:
                'Realizar a retirada total do valor das vendas em dinheiro e o fechamento do operador ' +
                'do POS, garantindo o correto registro, acondicionamento e depósito do valor no cofre.',
            responsavel: 'Gerente de Plantão ou Gerente de Quiosque',
            sistemas: ['POS'],
            blocos: [
                {
                    tipo: 'passo',
                    texto: 'No POS, acesse Menu Gerente.',
                    imagens: [
                        { arquivo: 'p10-1.webp', legenda: 'Tela principal do POS — o botão Menu Gerente fica no topo à direita.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                        'Selecione Sangria Total / Fechamento de Operador',
                    ],
                    imagens: [
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                        { arquivo: 'p29-3.webp', legenda: 'Menu Gerente com a opção Sangria Total / Fechamento de Operador.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                        'Confirme em SIM o logout do operador',
                    ],
                    imagens: [
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                        { arquivo: 'p30-3.webp', legenda: 'Confirmação do logout do operador.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'Escaneie o código de barras do envelope de sangria e aperte Enter. Em caso de não ' +
                        'leitura automática do código, os 9 dígitos devem ser inseridos manualmente. ' +
                        'Confira o número apresentado na tela e confirme.',
                    imagens: [
                        { arquivo: 'p23-1.webp', legenda: 'Campo do código do envelope, com teclado na tela para digitação manual.' },
                        { arquivo: 'p31-2.webp', legenda: 'Código do envelope digitado.' },
                        { arquivo: 'p31-3.webp', legenda: 'Confirmação do número do envelope (banana).' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'Confirme a sangria. O sistema realizará a sangria do valor total, imprimirá o ' +
                        'comprovante e abrirá a gaveta de dinheiro. Separe o valor correspondente ao fundo ' +
                        'do POS. Conte o valor restante — esse será o valor da Sangria Total.',
                    imagens: [
                        { arquivo: 'p32-1.webp', legenda: 'Confirmação da retirada do valor total em dinheiro.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto: 'O fundo não deve ser colocado no envelope de sangria.',
                },
                {
                    tipo: 'nota',
                    icone: 'assinatura',
                    texto: 'O comprovante deve ser assinado por:',
                    itens: ['Gerente responsável', 'Colaborador que estiver operando o POS'],
                },
                {
                    tipo: 'passo',
                    texto: 'Coloque no envelope:',
                    itens: ['Valor da Sangria Total', 'Comprovante assinado'],
                },
                {
                    tipo: 'nota',
                    icone: 'lacre',
                    texto: 'Lacre o envelope imediatamente e na presença do colaborador.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Deposite no cofre',
                    texto: 'Após finalizar a sangria, encaminhe-se diretamente à gerência e deposite o envelope no cofre.',
                    imagens: [
                        { arquivo: 'p33-1.webp', legenda: 'Envelope de sangria total lacrado, com o comprovante dentro.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    texto:
                        'A sangria deve ser depositada no cofre imediatamente após sua finalização. ' +
                        'Em hipótese alguma o envelope deve ser colocado no bolso, guardado em outro local ' +
                        'ou permanecer com o gerente.',
                },
            ],
            conclusao: {
                titulo: 'Sangria total / fechamento de operador concluído',
                itens: [
                    'Envelope identificado',
                    'Fundo separado',
                    'Valor da sangria conferido',
                    'Comprovante assinado',
                    'Valor e comprovante colocados no envelope',
                    'Envelope lacrado na presença do colaborador',
                    'Sangria depositada imediatamente no cofre',
                ],
            },
        },

        // ====================================================================
        {
            numero: '06',
            id: 'cancelamento-de-cupom',
            titulo: 'Cancelamento de Cupom (Nulo Pós-Total)',
            objetivo:
                'Cancelar um cupom fiscal já finalizado, quando aplicável, garantindo o correto ' +
                'registro e a justificativa da operação.',
            responsavel:
                'Gerente de Plantão ou Gerente de Quiosque, sob supervisão e autorização do Gerente de Plantão.',
            sistemas: ['POS'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto:
                        'O cancelamento é permitido somente para o último cupom registrado e quando ' +
                        'finalizado em dinheiro. Cupons finalizados em modalidades de pagamento TEF não ' +
                        'podem ser cancelados por este processo.',
                },
                {
                    tipo: 'passo',
                    texto: 'Após a finalização do pedido, no POS, clique em Cancela Cupom.',
                    imagens: [
                        { arquivo: 'p34-1.webp', legenda: 'Tela do POS após o pedido finalizado — a opção Cancela Cupom.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Confirme o cancelamento em SIM',
                        'Informe o número do gerente e a senha e confirme o cancelamento',
                    ],
                    imagens: [
                        { arquivo: 'p35-1.webp', legenda: 'Confirmação do cancelamento do último pedido.' },
                        { arquivo: 'p35-2.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p35-3.webp', legenda: 'Teclado — senha do gerente.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'O sistema imprimirá o cupom fiscal cancelado. Caso exista valor a ser devolvido em ' +
                        'dinheiro, realize a devolução ao cliente.',
                    imagens: [
                        { arquivo: 'p36-1.webp', legenda: 'Cupom fiscal de venda e cupom de anulação impressos.' },
                    ],
                },
                {
                    tipo: 'passo',
                    titulo: 'Preencha o Comprovante de Anulação',
                    texto: 'O Gerente responsável deve preencher o Comprovante de Anulação. O colaborador que estava operando o POS deve:',
                    itens: [
                        'Registrar por escrito o motivo do cancelamento',
                        'Assinar o comprovante',
                    ],
                    imagens: [
                        { arquivo: 'p37-1.webp', legenda: 'Comprovante de Anulação — frente, preenchida.' },
                        { arquivo: 'p37-2.webp', legenda: 'Verso — o motivo do cancelamento escrito à mão e as assinaturas.' },
                        { arquivo: 'p37-3.webp', legenda: 'Modelo de exemplo, com o cupom colado no comprovante.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'O Gerente responsável também deve assinar.',
                },
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    titulo: 'Onde conseguir o comprovante',
                    texto: 'Solicite na Central de Pedidos — Fornecedor: GIMBA — Código: 11020750.',
                },
                {
                    tipo: 'passo',
                    texto:
                        'Ao final do plantão, o Comprovante de Anulação deve ser anexado no verso do ' +
                        'fechamento financeiro correspondente.',
                    imagens: [
                        { arquivo: 'p38-1.webp', legenda: 'Onde cada documento é anexado no verso do fechamento financeiro.' },
                    ],
                },
            ],
            conclusao: {
                titulo: 'Cancelamento concluído',
                itens: [
                    'Cancelamento realizado dentro das condições permitidas',
                    'Cupom fiscal cancelado emitido',
                    'Valor devolvido ao cliente, quando aplicável',
                    'Motivo do cancelamento registrado',
                    'Colaborador e Gerente responsável assinaram',
                    'Comprovante anexado ao fechamento financeiro',
                ],
            },
        },

        // ====================================================================
        {
            numero: '07',
            id: 'retirada-de-petty-cash',
            titulo: 'Retirada de Petty Cash',
            objetivo:
                'Realizar a retirada de valores destinados ao Petty Cash, garantindo o correto ' +
                'registro e controle da movimentação.',
            responsavel: 'Gerente de Plantão',
            sistemas: ['POS', 'Site Grupo Correia'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    titulo: 'Dois sistemas',
                    texto: 'O processo de retirada deve ser registrado em dois sistemas: FLEX → Site Grupo Correia.',
                },
                { tipo: 'secao', titulo: 'Como fazer — FLEX' },
                {
                    tipo: 'passo',
                    texto: 'No POS, clique em Menu Gerente.',
                    imagens: [
                        { arquivo: 'p10-1.webp', legenda: 'Tela principal do POS — o botão Menu Gerente fica no topo à direita.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                        'Selecione Verba Emergencial',
                    ],
                    imagens: [
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                        { arquivo: 'p40-3.webp', legenda: 'Menu Gerente com a opção Verba Emergencial.' },
                    ],
                },
                {
                    tipo: 'passo',
                    itens: [
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                        'Confirme a autorização',
                    ],
                    imagens: [
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                        { arquivo: 'p41-3.webp', legenda: 'A Verba Emergencial exige autorização prévia da equipe de Auditoria.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'O sistema apresentará o valor disponível. Informe o valor que será retirado e confirme a operação.',
                    imagens: [
                        { arquivo: 'p42-1.webp', legenda: 'Valor da transferência para o Caixa E.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Após a confirmação, o sistema imprimirá o comprovante.',
                    imagens: [
                        { arquivo: 'p43-1.webp', legenda: 'Comprovante da Verba Emergencial impresso no POS.' },
                    ],
                },
                {
                    tipo: 'nota',
                    icone: 'pasta',
                    texto: 'Guarde o valor retirado na pasta de Petty Cash no banco.',
                },
                {
                    tipo: 'nota',
                    icone: 'anexo',
                    texto: 'O comprovante deve ser anexado e a retirada informada no fechamento financeiro.',
                },
                { tipo: 'secao', titulo: 'Como fazer — Site Grupo Correia' },
                {
                    tipo: 'passo',
                    texto:
                        'Acesse Financeiro → Petty Cash → Controle. Irá aparecer o valor a sacar (que deve ' +
                        'ser o mesmo sacado no POS); basta clicar em Confirmar Saque.',
                    imagens: [
                        { arquivo: 'p44-1.webp', legenda: 'Financeiro › Petty Cash › Controle — o card “Valor a sacar” e o botão Confirmar Saque.' },
                    ],
                },
            ],
            conclusao: {
                titulo: 'Retirada de Petty Cash concluída',
                itens: [
                    'Valor retirado registrado no sistema',
                    'Comprovante emitido',
                    'Valor guardado na pasta de Petty Cash',
                    'Comprovante anexado ao fechamento financeiro',
                    'Retirada informada no fechamento financeiro',
                    'Saque confirmado no Site Grupo Correia',
                ],
            },
        },

        // ====================================================================
        {
            numero: '08',
            id: 'recebimento-de-troco',
            titulo: 'Recebimento de Troco',
            objetivo: 'Conferir e registrar corretamente o recebimento do malote de troco nos sistemas do Grupo Correia e FLEX.',
            responsavel: 'Gerente de Plantão',
            sistemas: ['Site Grupo Correia', 'FLEX'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    titulo: 'Dois sistemas',
                    texto: 'O recebimento do troco deve ser registrado em dois sistemas: Site Grupo Correia → FLEX.',
                },
                { tipo: 'secao', titulo: 'Como fazer — Site Grupo Correia' },
                {
                    tipo: 'passo',
                    texto:
                        'No Site do Grupo Correia, acesse: Financeiro → Troco → Controle. Na aba Controle, ' +
                        'o sistema apresentará o valor do troco enviado ao restaurante. Abra o malote e ' +
                        'confira se o valor recebido corresponde ao valor informado no site.',
                    imagens: [
                        { arquivo: 'p45-1.webp', legenda: 'Financeiro › Troco › Controle no portal.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto: 'Confirme o valor físico antes de registrar o recebimento.',
                },
                {
                    tipo: 'passo',
                    texto: 'Após a conferência, clique em Recebido e confirme o recebimento.',
                    imagens: [
                        { arquivo: 'p46-1.webp', legenda: 'Relatório de Troco — o botão Recebido na linha do envio.' },
                        { arquivo: 'p46-2.webp', legenda: 'Confirmação do recebimento, com loja, data, nº do lacre e valor.' },
                        { arquivo: 'p47-1.webp', legenda: 'O lançamento do troco registrado no relatório.' },
                    ],
                },
                { tipo: 'secao', titulo: 'Como fazer — Sistema FLEX' },
                {
                    tipo: 'passo',
                    texto:
                        'Entre no FLEX com usuário e senha. Acesse: Flex Gestão Financeira → Lançamentos → ' +
                        'Lançamento de Troco Recebido.',
                    imagens: [
                        { arquivo: 'p47-2.webp', legenda: 'Login do FLEX.' },
                        { arquivo: 'p47-3.webp', legenda: 'Menu principal — Flex Gestão Financeira.' },
                        { arquivo: 'p47-4.webp', legenda: 'Flex Gestão Financeira — Lançamentos.' },
                        { arquivo: 'p47-5.webp', legenda: 'Lançamentos — Lançamento de Troco Recebido.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'Preencha a quantidade de cada moeda/cédula recebida. O sistema calcula ' +
                        'automaticamente o valor correspondente.',
                    imagens: [
                        { arquivo: 'p48-1.webp', legenda: 'Tabela de denominações do Lançamento de Troco Recebido.' },
                    ],
                },
                {
                    tipo: 'exemplo',
                    linhas: [
                        { rotulo: '100 moedas de R$ 0,05', valor: 'R$ 5,00' },
                    ],
                    nota: 'Repita o processo até registrar todas as moedas e cédulas do malote.',
                },
                {
                    tipo: 'passo',
                    titulo: 'No campo Testemunha',
                    itens: [
                        'Havendo dois gerentes na loja, informe o outro gerente.',
                        'Caso esteja sozinho, informe o Gerente da Unidade, que poderá verificar o processo posteriormente.',
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Preencha:',
                    itens: [
                        'Número do malote: número do lacre amarelo do malote recebido.',
                        'Número da guia de troco: repetir o número do malote.',
                    ],
                    depois: 'Clique em Salvar e confirme.',
                    imagens: [
                        { arquivo: 'p49-1.webp', legenda: 'Testemunha, nº do malote e nº da guia preenchidos.' },
                        { arquivo: 'p49-2.webp', legenda: 'Aviso de alterações pendentes — o lançamento não poderá ser alterado depois.' },
                        { arquivo: 'p49-3.webp', legenda: 'Lançamento de Troco Recebido realizado com sucesso.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Imprima o comprovante do lançamento.',
                    imagens: [
                        { arquivo: 'p50-1.webp', legenda: 'Impressão do comprovante do lançamento.' },
                    ],
                },
                {
                    tipo: 'nota',
                    icone: 'assinatura',
                    texto: 'O documento deve ser assinado por:',
                    itens: ['Gerente responsável', 'Testemunha'],
                },
                {
                    tipo: 'nota',
                    icone: 'anexo',
                    texto: 'Após as assinaturas, anexe o comprovante ao financeiro do restaurante.',
                },
            ],
            conclusao: {
                titulo: 'Recebimento de troco concluído',
                itens: [
                    'Valor físico conferido com o Site Grupo Correia',
                    'Recebimento confirmado no site',
                    'Moedas e cédulas registradas no FLEX',
                    'Testemunha informada',
                    'Número do malote registrado',
                    'Comprovante impresso e assinado',
                    'Comprovante anexado ao financeiro',
                ],
            },
        },

        // ====================================================================
        {
            numero: '09',
            id: 'devolucao-de-troco',
            titulo: 'Devolução de Troco',
            objetivo: 'Devolver ao banco o mesmo valor recebido como troco, mantendo o saldo do banco do restaurante inalterado.',
            responsavel: 'Gerente de Plantão',
            sistemas: ['FLEX'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto: 'Após o recebimento do troco, a devolução do mesmo valor deve ser realizada imediatamente.',
                },
                {
                    tipo: 'passo',
                    texto: 'Após receber o troco, substitua o valor recebido por cédulas de maior valor, preferencialmente:',
                    itens: ['R$ 50,00', 'R$ 100,00'],
                    depois:
                        'O valor total deve ser exatamente igual ao troco recebido. Coloque o valor em um ' +
                        'envelope de sangria e lacre para depositar imediatamente após a finalização do processo.',
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    texto:
                        'O recebimento e a devolução devem ter o mesmo valor. Esse processo é fundamental ' +
                        'para que o saldo do banco do restaurante não seja alterado.',
                },
                {
                    tipo: 'exemplo',
                    linhas: [
                        { rotulo: 'Saldo inicial do banco', valor: 'R$ 3.000,00' },
                        { rotulo: 'Troco recebido', valor: '+ R$ 1.000,00' },
                        { rotulo: 'Troco devolvido', valor: '− R$ 1.000,00' },
                        { rotulo: 'Saldo final do banco', valor: 'R$ 3.000,00', destaque: true },
                    ],
                },
                { tipo: 'secao', titulo: 'FLEX' },
                {
                    tipo: 'passo',
                    texto:
                        'Acesse o FLEX com usuário e senha. Siga o caminho: Flex Gestão Financeira → ' +
                        'Lançamentos → Devolução de Troco. Registre a devolução preenchendo valor, ' +
                        'testemunha e código do envelope de sangria e clique em Salvar.',
                    imagens: [
                        { arquivo: 'p47-2.webp', legenda: 'Login do FLEX.' },
                        { arquivo: 'p47-3.webp', legenda: 'Menu principal — Flex Gestão Financeira.' },
                        { arquivo: 'p47-4.webp', legenda: 'Flex Gestão Financeira — Lançamentos.' },
                        { arquivo: 'p52-4.webp', legenda: 'Lançamentos — Devolução de Troco.' },
                        { arquivo: 'p52-5.webp', legenda: 'Valor, testemunha, data e código da banana preenchidos.' },
                        { arquivo: 'p52-6.webp', legenda: 'Aviso de alterações pendentes — a devolução não poderá ser alterada depois.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Imprima o comprovante da devolução e assine.',
                    imagens: [
                        { arquivo: 'p53-1.webp', legenda: 'Confirmação da devolução de troco.' },
                        { arquivo: 'p53-2.webp', legenda: 'Impressão do comprovante da devolução.' },
                    ],
                },
                {
                    tipo: 'nota',
                    icone: 'anexo',
                    texto: 'Anexe o comprovante ao fechamento financeiro correspondente.',
                },
            ],
            conclusao: {
                titulo: 'Devolução de troco concluída',
                itens: [
                    'Valor recebido convertido em cédulas de maior valor',
                    'Mesmo valor depositado no banco imediatamente',
                    'Saldo do banco preservado',
                    'Devolução registrada no FLEX',
                    'Comprovante impresso e assinado',
                    'Comprovante anexado ao fechamento financeiro',
                ],
            },
        },

        // ====================================================================
        {
            numero: '10',
            id: 'conferencia-do-saldo-do-banco',
            titulo: 'Conferência do Saldo do Banco',
            objetivo:
                'Conferir os valores existentes no banco do restaurante e garantir que o saldo ' +
                'permaneça correto entre os plantões.',
            responsavel: 'Gerente de Plantão',
            quando: 'Ao assumir e ao finalizar a responsabilidade pelo plantão.',
            blocos: [
                {
                    tipo: 'tabela',
                    titulo: 'Frequência',
                    colunas: ['Tipo de loja', 'Momentos', 'Nº de conferências'],
                    linhas: [
                        ['🏬 Loja sem madrugada', 'Abertura → Fechamento', '2 conferências'],
                        ['🌙 Loja com madrugada', 'Abertura → Passagem de plantão → Fechamento da madrugada', '3 conferências (a passagem deve ser realizada pelos dois plantões juntos)'],
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    titulo: 'Plantão da madrugada',
                    texto:
                        'Mesmo que o Gerente de Plantão da madrugada já esteja no dia seguinte, ao realizar ' +
                        'o controle de fechamento deverá registrar a data referente ao dia anterior.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Antes de iniciar a conferência',
                    itens: [
                        'Mantenha a gerência organizada',
                        'Certifique-se de que o ambiente esteja seguro',
                        'Não permita a presença de colaboradores não autorizados durante a contagem',
                    ],
                    iconeItens: 'check',
                },
                {
                    tipo: 'passo',
                    texto: 'No documento de controle, registre:',
                    itens: ['Data', 'Nome do Gerente de Plantão'],
                    depois: 'Caso haja testemunha, ela deverá assinar no campo correspondente.',
                    imagens: [
                        { arquivo: 'p55-1.webp', legenda: 'Controle de Fluxo de Caixa — a data do controle.' },
                        { arquivo: 'p55-2.webp', legenda: 'O bloco de Abertura: gerente do plantão e coordenador/testemunha.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Realize separadamente a contagem de:',
                    itens: ['💵 Cédulas', '🪙 Moedas', '💰 Fundos'],
                    depois:
                        'Registre cada valor no caderno e totalize a contagem. Compare o total apurado com ' +
                        'o saldo do banco definido para o restaurante.',
                    imagens: [
                        { arquivo: 'p56-1.webp', legenda: 'Saldo Banco e Total Apurado — os dois números que têm de fechar.' },
                    ],
                },
                {
                    tipo: 'exemplo',
                    linhas: [
                        { rotulo: 'Total apurado', valor: '= Saldo do banco', destaque: true },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    titulo: 'Divergência?',
                    texto:
                        'O saldo do banco não deve sofrer alterações. Caso seja identificada qualquer ' +
                        'diferença, registre a divergência e comunique imediatamente ao Gerente da Unidade.',
                },
            ],
            conclusao: {
                titulo: 'Conferência concluída',
                itens: [
                    'Ambiente seguro para contagem',
                    'Data e Gerente de Plantão registrados',
                    'Cédulas, moedas e fundos contados separadamente',
                    'Valores registrados e totalizados',
                    'Total conferido com o saldo do banco',
                    'Divergências sinalizadas e comunicadas, quando houver',
                ],
            },
        },

        // ====================================================================
        {
            numero: '11',
            id: 'solicitacao-de-estorno',
            titulo: 'Solicitação de Estorno',
            objetivo:
                'Solicitar a devolução total ou parcial de uma compra paga via TEF, garantindo o ' +
                'registro correto das informações do cliente e da transação.',
            responsavel: 'Gerente de Plantão',
            sistemas: ['Site Grupo Correia'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    titulo: 'Quando utilizar',
                    texto:
                        'Quando houver necessidade de devolver, total ou parcialmente, o valor de uma ' +
                        'compra realizada via TEF.',
                },
                {
                    tipo: 'passo',
                    texto: 'O processo acontece em duas etapas: ① Coleta dos dados do cliente → ② Registro no Site Grupo Correia.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Preencha a Solicitação de Estorno',
                    texto:
                        'Utilize o Caderno de Solicitação de Estorno — Anexo 1. Preencha todas as ' +
                        'informações solicitadas, incluindo:',
                    itens: [
                        'Dados da compra',
                        'Valor a ser devolvido',
                        'Dados da forma de pagamento',
                        'Dados de identificação e contato do cliente',
                    ],
                    imagens: [
                        { arquivo: 'p58-1.webp', legenda: 'Caderno de Solicitação de Estorno de Cartão — o formulário do Anexo 1.' },
                    ],
                    referencia: { capitulo: '17', texto: 'Ver o Anexo 1 para imprimir o formulário' },
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto:
                        'Confira se todos os campos estão preenchidos antes de finalizar o atendimento ao ' +
                        'cliente. Essas informações serão necessárias para registrar a solicitação no site.',
                },
                { tipo: 'secao', titulo: 'No Site Grupo Correia' },
                {
                    tipo: 'passo',
                    texto: 'Faça login e acesse: Financeiro → Estorno de Venda.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Registre a solicitação',
                    texto: 'Utilizando os dados coletados no Caderno de Solicitação de Estorno:',
                    itens: [
                        'Preencha todas as informações solicitadas e anexe o cupom fiscal',
                        'Confira o valor do estorno',
                        'Clique em Finalizar solicitação',
                    ],
                    imagens: [
                        { arquivo: 'p59-1.webp', legenda: 'Financeiro › Estorno de Venda › Solicitar Estorno no portal.' },
                    ],
                },
            ],
            conclusao: {
                titulo: 'Solicitação de estorno concluída',
                itens: [
                    'Dados da compra registrados',
                    'Valor do estorno informado',
                    'Identificação e contato do cliente preenchidos',
                    'Solicitação registrada no Site Grupo Correia',
                ],
            },
        },

        // ====================================================================
        {
            numero: '12',
            id: 'registro-de-nulos-em-progresso',
            titulo: 'Registro de Nulos em Progresso',
            objetivo:
                'Registrar no Site Grupo Correia todos os Nulos em Progresso realizados no ' +
                'restaurante, garantindo o controle e a rastreabilidade dos cancelamentos.',
            responsavel: 'Gerente de Plantão',
            quando: 'No momento em que ocorrer o cancelamento.',
            sistemas: ['Site Grupo Correia'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto: 'Todo Nulo em Progresso deve ser registrado no Site Grupo Correia no momento do cancelamento.',
                },
                {
                    tipo: 'passo',
                    texto: 'No Site Grupo Correia, faça login e acesse: Financeiro → Nulos em Progresso.',
                    imagens: [
                        { arquivo: 'p60-1.webp', legenda: 'Financeiro › Nulos em Progresso › Registrar Nulo no portal.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Informe todos os dados solicitados:',
                    itens: [
                        'Data',
                        'Hora',
                        'Loja',
                        'Gerente',
                        'POS',
                        'Valor',
                        'Forma de pagamento',
                        'Se o valor foi estornado para o cliente',
                        'Se o valor foi cobrado novamente do cliente',
                    ],
                    depois: 'Anexe a foto da tela do pedido cancelado.',
                    imagens: [
                        { arquivo: 'p61-1.webp', legenda: 'Formulário do Registro de Nulos em Progresso, com a foto da tela e o botão Registrar nulo.' },
                    ],
                },
                {
                    tipo: 'nota',
                    icone: 'anexo',
                    texto: 'Confira se a imagem está legível e corresponde ao cancelamento que está sendo registrado.',
                },
                {
                    tipo: 'passo',
                    texto: 'Confira todas as informações preenchidas e clique em Registrar Nulo.',
                },
            ],
            conclusao: {
                titulo: 'Registro de nulo em progresso concluído',
                itens: [
                    'Data e hora informadas',
                    'Loja, gerente e POS registrados',
                    'Valor e forma de pagamento informados',
                    'Estorno ao cliente informado',
                    'Nova cobrança ao cliente informada',
                    'Foto do cancelamento anexada',
                    'Nulo registrado no Site Grupo Correia',
                ],
            },
        },

        // ====================================================================
        {
            numero: '13',
            id: 'lancamento-de-petty-cash',
            titulo: 'Lançamento de Petty Cash',
            objetivo: 'Registrar corretamente as pequenas despesas realizadas com o valor mensal disponibilizado para Petty Cash.',
            responsavel: 'Gerente de Plantão',
            sistemas: ['Site Grupo Correia'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto: 'No momento da compra, solicite o cupom fiscal comum ou, quando não estiver disponível, um recibo.',
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    titulo: 'Não utilizar',
                    texto:
                        'Não deve ser emitida DANFE ou Nota Fiscal de Serviço utilizando o CNPJ da empresa ' +
                        'para despesas de Petty Cash.',
                },
                {
                    tipo: 'passo',
                    texto: 'No Site do Grupo Correia, acesse: Financeiro → Petty Cash → Registrar Despesa.',
                },
                {
                    tipo: 'passo',
                    texto: 'Informe os dados da despesa e anexe o comprovante:',
                    itens: [
                        'Loja',
                        'Data',
                        'Categoria da despesa',
                        'Número do cupom',
                        'Valor',
                        'Quantidade',
                    ],
                    imagens: [
                        { arquivo: 'p64-1.webp', legenda: 'Financeiro › Petty Cash › Registrar Despesa no portal.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto: 'Anexe a foto ou o arquivo PDF do cupom fiscal ou recibo correspondente à compra.',
                },
                {
                    tipo: 'nota',
                    icone: 'anexo',
                    texto: 'Confira se o documento está legível antes de anexá-lo.',
                },
                {
                    tipo: 'passo',
                    texto: 'Confira as informações preenchidas e clique em Enviar para aprovação.',
                },
            ],
            conclusao: {
                titulo: 'Lançamento de Petty Cash concluído',
                itens: [
                    'Dados da despesa preenchidos',
                    'Cupom fiscal ou recibo conferido',
                    'Comprovante anexado',
                    'Despesa registrada no Site Grupo Correia',
                ],
            },
        },

        // ====================================================================
        {
            numero: '14',
            id: 'recebimento-prosegur',
            titulo: 'Recebimento Prosegur',
            objetivo:
                'Realizar a preparação e entrega dos envelopes de sangria à Prosegur, garantindo ' +
                'segurança, conferência e registro correto da operação.',
            responsavel: 'Gerente de Plantão',
            sistemas: ['FLEX'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto:
                        'O processo deve ser realizado somente após a chegada da Prosegur ao restaurante. ' +
                        'A operação deve ser feita por duas pessoas: o responsável e uma testemunha.',
                },
                {
                    tipo: 'passo',
                    texto: 'Faça login no sistema FLEX e acesse: Flex Gestão Financeira → Lançamentos → Depósito de Bananas.',
                    imagens: [
                        { arquivo: 'p47-2.webp', legenda: 'Login do FLEX.' },
                        { arquivo: 'p47-3.webp', legenda: 'Menu principal — Flex Gestão Financeira.' },
                        { arquivo: 'p47-4.webp', legenda: 'Flex Gestão Financeira — Lançamentos.' },
                        { arquivo: 'p65-4.webp', legenda: 'Lançamentos — Depósito de bananas.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Preencha:',
                    itens: ['Período', 'Número da Guia de Embarque'],
                    depois:
                        'Clique em Gerar a Guia de Embarque. Confira as informações apresentadas e clique ' +
                        'em Salvar. Após a confirmação, o sistema informará que a guia foi gerada com sucesso.',
                    imagens: [
                        { arquivo: 'p66-1.webp', legenda: 'Depósito de bananas — período, nº da guia e o botão Gerar a guia de embarque.' },
                        { arquivo: 'p66-2.webp', legenda: 'Aviso de alterações pendentes — a guia não poderá ser alterada depois.' },
                        { arquivo: 'p66-3.webp', legenda: 'Depósito de bananas registrado com sucesso.' },
                    ],
                },
                {
                    tipo: 'passo',
                    titulo: 'Imprima o Guia de Embarque',
                    imagens: [
                        { arquivo: 'p67-1.webp', legenda: 'Impressão da Guia de embarque de valores entregues.' },
                    ],
                },
                {
                    tipo: 'passo',
                    titulo: 'Registre as informações na Guia de Embarque',
                    texto: 'Na guia impressa:',
                    itens: [
                        'Registre a quantidade total de envelopes de sangria com valor.',
                        'O responsável e a testemunha devem assinar o documento.',
                        'Após as assinaturas, anexe a guia ao fechamento financeiro do dia.',
                    ],
                    imagens: [
                        { arquivo: 'p68-1.webp', legenda: 'Totais da guia e os campos de assinatura do responsável e da testemunha.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto:
                        'O relatório também contabiliza envelopes referentes a sangrias zeradas. Para a ' +
                        'quantidade real, considere somente os envelopes de sangria que possuem valor.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Prepare o malote Prosegur',
                    texto: 'Preencha o malote amarelo com:',
                    itens: [
                        'Cliente: Mc Donald’s/PeG ou Hunger',
                        'Remetente: sigla da loja',
                        'Destinatário: Prosegur',
                        'Data e valor',
                    ],
                    imagens: [
                        { arquivo: 'p69-1.webp', legenda: 'Malote amarelo da Prosegur preenchido, com cliente, remetente, destinatário, data e valor.' },
                    ],
                },
                {
                    tipo: 'passo',
                    titulo: 'Confira e deposite os envelopes',
                    texto:
                        'Faça a contagem dos envelopes de sangria um a um e registre a quantidade apurada ' +
                        'na Guia de Embarque.',
                    itens: [
                        'Confira a quantidade de envelopes, e não os valores individuais.',
                        'Deposite os envelopes no malote amarelo.',
                        'Lacre o malote.',
                        'Entregue o malote ao coletor da Prosegur.',
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto:
                        'Se houver divergência entre a quantidade apurada pelo sistema e a quantidade ' +
                        'contada e enviada para a Prosegur, finalize a operação e posteriormente comunique ' +
                        'o Gerente da Unidade.',
                },
                {
                    tipo: 'aviso',
                    nivel: 'critico',
                    titulo: 'Segurança durante o processo',
                    texto: 'Realize toda a operação de forma organizada e segura.',
                    itens: [
                        'Mantenha o local organizado',
                        'Posicione-se de forma que a câmera tenha visão limpa da operação',
                        'Evite celular, conversas paralelas e outras distrações',
                        'Realize o processo sempre com a testemunha',
                    ],
                },
            ],
            conclusao: {
                titulo: 'Recebimento Prosegur concluído',
                itens: [
                    'Guia de Embarque gerada no FLEX',
                    'Guia impressa e assinada',
                    'Guia anexada ao financeiro do dia',
                    'Quantidade real de envelopes conferida',
                    'Sangrias zeradas desconsideradas da quantidade real',
                    'Malote Prosegur preenchido',
                    'Processo realizado com responsável e testemunha',
                ],
            },
        },

        // ====================================================================
        {
            numero: '15',
            id: 'preenchimento-caderno-financeiro',
            titulo: 'Preenchimento do Caderno Financeiro',
            objetivo:
                'Registrar e organizar as informações financeiras do plantão, garantindo a ' +
                'conferência dos valores, movimentações e documentos que compõem o fechamento financeiro.',
            responsavel: 'Gerente de Plantão',
            quando: 'Ao final de cada plantão.',
            blocos: [
                {
                    tipo: 'passo',
                    titulo: 'Separe os relatórios e comprovantes',
                    texto: 'Antes de iniciar o preenchimento do Caderno Financeiro, reúna os documentos referentes ao plantão:',
                    itens: [
                        'Relatório de Vendas / Fita dos POS',
                        'Relatório de Segmentos — FLEX',
                        'Comprovantes de Anulação, quando houver',
                        'Relatório GPRS, quando houver',
                        'Relatório de Bananas',
                        'Relatório de Quebra de Caixa, quando aplicável à versão do sistema',
                    ],
                },
                {
                    tipo: 'passo',
                    titulo: 'Preencha as informações do plantão',
                    texto:
                        'Utilizando os relatórios e comprovantes, preencha os respectivos campos do Caderno ' +
                        'Financeiro. Registre as informações referentes ao plantão, incluindo:',
                    itens: [
                        'Sangria em dinheiro',
                        'Erros e nulos',
                        'Relatórios GPRS',
                        'Número de bananas',
                        'Demais movimentações e informações solicitadas no caderno',
                    ],
                },
                {
                    tipo: 'imagens',
                    titulo: 'Exemplo preenchido',
                    imagens: [
                        { arquivo: 'p72-1.webp', legenda: 'Frente — Controle Diário com o Relatório de Segmentos FLEX e a Análise Gerencial.' },
                        { arquivo: 'p73-1.webp', legenda: 'Verso — Relatório de bananas, Quebra de Caixa e as fitas anexadas.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto:
                        'Os valores registrados no Caderno Financeiro devem estar de acordo com os ' +
                        'relatórios e documentos utilizados no fechamento. Qualquer divergência identificada ' +
                        'deve ser verificada antes da conclusão do processo.',
                },
            ],
            conclusao: {
                titulo: 'Caderno Financeiro concluído',
                itens: [
                    'Relatórios necessários separados',
                    'Comprovantes reunidos',
                    'Informações do plantão registradas',
                    'Numerários preenchidos',
                    'Erros e movimentações registrados',
                    'Valores conferidos com os respectivos relatórios',
                    'Documentação do fechamento organizada',
                ],
            },
        },

        // ====================================================================
        {
            numero: '16',
            id: 'fechamento-do-dia',
            titulo: 'Fechamento do Dia',
            objetivo:
                'Encerrar corretamente a operação do dia, garantindo o fechamento dos operadores de ' +
                'todos os POS e totens de autoatendimento antes do fechamento do dia no Waystation.',
            responsavel: 'Gerente de Plantão',
            quando: 'Ao final da operação do dia.',
            sistemas: ['POS', 'Waystation'],
            blocos: [
                {
                    tipo: 'aviso',
                    nivel: 'importante',
                    texto:
                        'Antes de realizar o fechamento do dia no Waystation, todos os operadores dos POS e ' +
                        'totens devem estar fechados.',
                },
                {
                    tipo: 'passo',
                    texto:
                        'Realize a Sangria Total / Fechamento de Operador de todos os POS utilizados durante ' +
                        'a operação.',
                    referencia: { capitulo: '05', texto: 'Siga o processo do capítulo 05 deste manual' },
                },
                {
                    tipo: 'passo',
                    titulo: 'Feche os totens de autoatendimento (NGK)',
                    itens: [
                        'No POS, clique em Menu Gerente',
                        'Coloque o número do gerente',
                        'Coloque a senha do gerente',
                    ],
                    imagens: [
                        { arquivo: 'p10-1.webp', legenda: 'Tela principal do POS — o botão Menu Gerente fica no topo à direita.' },
                        { arquivo: 'p11-1.webp', legenda: 'Teclado — número do gerente.' },
                        { arquivo: 'p11-2.webp', legenda: 'Teclado — senha do gerente.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Acesse Menu NGK.',
                    imagens: [
                        { arquivo: 'p77-1.webp', legenda: 'Menu Gerente com a opção Menu NGK.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto: 'Selecione e feche os NGKs correspondentes.',
                    imagens: [
                        { arquivo: 'p78-1.webp', legenda: 'Menu NGK — os botões de abrir e fechar operador de cada totem.' },
                    ],
                },
                {
                    tipo: 'aviso',
                    nivel: 'atencao',
                    texto:
                        'Certifique-se de que todos os totens de autoatendimento estejam fechados antes de ' +
                        'realizar o fechamento do dia.',
                },
                {
                    tipo: 'passo',
                    titulo: 'Acesse o Waystation',
                    texto:
                        'Após o fechamento de todos os POS e totens, acesse o Waystation. Selecione todos os ' +
                        'POS e totens, clique em Operações Gerenciais e confirme número e senha do gerente.',
                    imagens: [
                        { arquivo: 'p07-1.webp', legenda: 'Todos os POS e totens selecionados e o botão “Operações de Gerente”.' },
                        { arquivo: 'p08-1.webp', legenda: 'Enter the manager id — número do gerente.' },
                        { arquivo: 'p08-2.webp', legenda: 'Enter the password — senha do gerente.' },
                    ],
                },
                {
                    tipo: 'passo',
                    texto:
                        'Clique em Fechamento do Dia Forçado, confirme a data e clique em OK. O Waystation ' +
                        'apresentará a confirmação do fechamento. Confira se o processo foi concluído para os ' +
                        'POS e totens selecionados.',
                    imagens: [
                        { arquivo: 'p80-1.webp', legenda: 'Os botões de fechamento do dia (Regular e Forçado).' },
                        { arquivo: 'p08-4.webp', legenda: 'A data do fechamento no formato yyyyMMdd.' },
                        { arquivo: 'p80-3.webp', legenda: 'Confirmação do Waystation: “fechamento do dia da loja forçado bem sucedido”.' },
                    ],
                },
            ],
            conclusao: {
                titulo: 'Fechamento do dia concluído',
                itens: [
                    'Operadores de todos os POS fechados',
                    'Operadores dos totens NGK fechados',
                    'Todos os POS e totens selecionados no Waystation',
                    'Fechamento Forçado do Dia realizado',
                    'Confirmação do sistema verificada',
                ],
            },
        },

        // ====================================================================
        {
            numero: '17',
            id: 'anexo-1-solicitacao-de-estorno',
            titulo: 'Anexo 1 — Solicitação de Estorno',
            anexo: true,
            objetivo:
                'Formulário do Caderno de Solicitação de Estorno de Cartão, para impressão. ' +
                'É onde os dados do cliente e da transação são coletados antes do registro no portal.',
            blocos: [
                {
                    tipo: 'passo',
                    texto:
                        'Duas vias por folha. Imprima e mantenha na gerência para uso durante o ' +
                        'atendimento ao cliente.',
                    referencia: { capitulo: '11', texto: 'O processo completo está no capítulo 11' },
                    imagens: [
                        { arquivo: 'p82-1.webp', legenda: 'Solicitação de Estorno de Cartão — duas vias por folha, prontas para impressão.' },
                    ],
                },
            ],
        },
    ],
};

export default manual;
